import { Alert } from '@mui/material';
import { DataGrid, DataGridProps, GridSortDirection } from '@mui/x-data-grid';
import { columns } from './columns';
import { NoCampaigns } from './NoCampaigns';
import { LCI_GREEN_COLOR } from '@utils/colorGenerator';
import { useContext, useState } from 'react';
import { CampaignListAttribute } from '@features/campaign-list/slice/schemas';
import { FilterContext } from '@features/campaign-list/stores/filter-context';
import { useCampaignList } from '@features/campaign-list/hooks/useCampaignList';
import { useTrends } from '@features/campaign-list/hooks/useTrends';
import { campaignListRowFromEntry } from '@features/campaign-list/utils/campaign-list-row-from-entry';
import { useLocalStorage } from '@utils/hooks';

const PAGE_SIZES = [10, 25, 50];
const DEFAULT_PAGE_SIZE = PAGE_SIZES[0];

const isGridSortDirection = (value: unknown): value is GridSortDirection =>
  value === 'asc' || value === 'desc';

const isCampaignListAttribute = (
  value: unknown
): value is CampaignListAttribute => columns.some(c => c.field === value);

export const CampaignDataGrid = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortDirection, setSortDirection] = useLocalStorage<GridSortDirection>(
    'campaignList:sortDirection',
    'desc',
    isGridSortDirection
  );
  const [sortKey, setSortKey] = useLocalStorage<CampaignListAttribute>(
    'campaignList:sortKey',
    'visitLift',
    isCampaignListAttribute
  );
  const { filterState } = useContext(FilterContext);

  const {
    isFetching: loadingByReportType,
    error,
    campaignList,
    total: rowCountByReportType,
  } = useCampaignList(filterState, {
    page,
    pageSize,
    sortKey,
    sortDirection,
  });

  const trendsHook = useTrends(filterState, campaignList);

  const rowsByReportType = campaignList.map(cl =>
    campaignListRowFromEntry(cl, trendsHook)
  );

  if (error) return <Alert severity="error">Error loading campaign list</Alert>;

  const onSortModelChange: DataGridProps['onSortModelChange'] = model => {
    const newSortField = (model[0]?.field ??
      'visitLift') as CampaignListAttribute;
    const newSortDirection = model[0]?.sort ?? 'asc';
    setPage(0);
    setSortDirection(newSortDirection);
    setSortKey(newSortField);
  };

  const onPaginationModelChange: DataGridProps['onPaginationModelChange'] =
    model => {
      setPage(model.page);
      setPageSize(model.pageSize);
    };

  return (
    <DataGrid
      loading={loadingByReportType}
      rows={rowsByReportType}
      getRowId={row => `${row.campaignId}-${row.reportId}`}
      columns={columns}
      paginationMode="server"
      sortingMode="server"
      pageSizeOptions={PAGE_SIZES}
      onPaginationModelChange={onPaginationModelChange}
      rowCount={rowCountByReportType ?? 0}
      sortingOrder={['desc', 'asc']}
      paginationModel={{ page, pageSize }}
      onSortModelChange={onSortModelChange}
      sortModel={[{ sort: sortDirection, field: sortKey }]}
      slots={{ noRowsOverlay: NoCampaigns }}
      disableRowSelectionOnClick
      sx={{
        height: 'auto',
        backgroundColor: 'white',
        padding: '1rem',
        a: {
          color: LCI_GREEN_COLOR,
          textDecoration: 'underline',
        },
        '--DataGrid-overlayHeight': '150px',
        'a:hover': {
          color: LCI_GREEN_COLOR,
        },
      }}
    />
  );
};
